import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BriefingBar } from "@/components/BriefingBar";
import BackToTop from "@/components/BackToTop";
import { DisclaimerBar } from "@/components/Disclaimer";
import StickyMiniNav from "@/components/StickyMiniNav";
import { Menu, X } from "lucide-react";

const navLinkClass = ({ isActive }) =>
  `font-mono text-xs uppercase tracking-[0.2em] px-3 py-2 border border-transparent whitespace-nowrap hover:border-ink transition-colors ${
    isActive ? "border-ink bg-ink text-white" : "text-ink"
  }`;

/** Compact style for the desktop marketing nav: six links must share the
    header row with Sign in + the CTA, so these run tighter than navLinkClass. */
const desktopNavClass =
  "font-mono text-[11px] uppercase tracking-[0.12em] px-2 py-2 whitespace-nowrap border border-transparent hover:border-ink transition-colors text-ink";

/** Desktop header marketing nav (logged-out visitors, >=xl; the hamburger
    carries the same menu at every width). Anchor entries use plain Link,
    since NavLink active-matching misfires on hash URLs. */
const DESKTOP_PUBLIC_NAV = [
  { to: "/#how-it-works", label: "How It Works", testid: "nav-how-it-works", anchor: true },
  { to: "/#who-its-for", label: "Who It's For", testid: "nav-who-its-for", anchor: true },
  { to: "/pricing#counsel-comparison", label: "CLARITY + Your Counsel", testid: "nav-comparison", anchor: true },
  { to: "/pricing", label: "Pricing", testid: "nav-pricing" },
  { to: "/faq", label: "FAQ", testid: "nav-faq" },
];

// Close the mobile drawer whenever the route changes
const useRouteCloseEffect = (path, close, open) => {
  const prev = useRef(path);
  useEffect(() => {
    if (open && prev.current !== path) close();
    prev.current = path;
  }, [path, open, close]);
};

const NavItem = ({ to, children, testid, onClick }) => (
  <NavLink to={to} data-testid={testid} className={navLinkClass} onClick={onClick}>
    {children}
  </NavLink>
);

const MobileNavItem = ({ to, children, testid, onClick, end }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testid}
    onClick={onClick}
    className={({ isActive }) =>
      `block w-full px-4 py-4 font-mono text-sm uppercase tracking-[0.2em] border-b border-rule ${
        isActive ? "bg-ink text-white" : "text-ink hover:bg-paper"
      }`
    }
  >
    {children}
  </NavLink>
);

/** Mobile drawer item for homepage section anchors (e.g. /#how-it-works).
    Plain Link (no active-state matching, which misfires on hash URLs); the
    global ScrollManager smooth-scrolls to the anchor after navigation. */
const MobileAnchorItem = ({ to, children, testid, onClick }) => (
  <Link
    to={to}
    data-testid={testid}
    onClick={onClick}
    className="block w-full px-4 py-4 font-mono text-sm uppercase tracking-[0.2em] border-b border-rule text-ink hover:bg-paper"
  >
    {children}
  </Link>
);

/** Full public mobile navigation: homepage section anchors + top-level pages.
    Every item closes the drawer immediately on tap (onClick={close}). */
const PUBLIC_MOBILE_NAV = [
  { to: "/", label: "Home", testid: "m-nav-home", exact: true },
  { to: "/#how-it-works", label: "How It Works", testid: "m-nav-how-it-works", anchor: true },
  { to: "/#who-its-for", label: "Who It's For", testid: "m-nav-who-its-for", anchor: true },
  // The counsel comparison section ("CLARITY and your counsel. Better
  // together.") lives on the pricing page; this deep-links precisely to it.
  // The homepage comparison section has its own /#comparison anchor.
  { to: "/pricing#counsel-comparison", label: "CLARITY + Your Counsel", testid: "m-nav-comparison", anchor: true },
  { to: "/pricing", label: "Pricing", testid: "m-nav-pricing" },
  { to: "/faq", label: "FAQ", testid: "m-nav-faq" },
];

export default function SiteShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  // Close the mobile drawer whenever the route changes
  useRouteCloseEffect(location.pathname, close, open);

  // Home and pricing only: the founder ruled it out everywhere else.
  const showBriefingBar = ["/", "/pricing"].includes(location.pathname);

  /* /start is a conversion page: the CLARITY + Your Counsel link is
     dropped from its header row so the only header choices are Sign in and
     Start Assessment. It stays in the mobile drawer. */
  const desktopNav =
    location.pathname === "/start"
      ? DESKTOP_PUBLIC_NAV.filter((item) => item.testid !== "nav-comparison")
      : DESKTOP_PUBLIC_NAV;

  /* /survey is a single-task page: the founder asked for no navigation away
     from it except the site header, so the footer navigation and the sticky
     mini-nav are dropped there. */
  const surveyOnly = location.pathname === "/survey";

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {showBriefingBar && <BriefingBar />}
      <header className="border-b border-ink relative">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-4 sm:py-5 flex items-center justify-between gap-3">
          <Link
            to="/"
            data-testid="brand-link"
            onClick={close}
            className="flex items-center gap-2.5 min-w-0"
          >
            <img
              src="/brand/initials-navy.png"
              alt="W3RC monogram"
              data-testid="brand-logo"
              className="h-6 sm:h-7 w-auto shrink-0"
            />
            <span className="font-serif text-xl sm:text-2xl font-semibold tracking-tighter leading-none whitespace-nowrap">
              Web3 Ready CLARITY<sup className="text-[0.45em] align-super ml-0.5">™</sup>
            </span>
          </Link>

          {/* DESKTOP NAV: stripped to essentials. All marketing links
              (Starter Kit, Pricing, FAQ) live in the footer navigation. */}
          <nav className="hidden lg:flex items-center gap-1">
            {user ? (
              <>
                <NavItem to="/dashboard" testid="nav-dashboard">Dashboard</NavItem>
                <NavItem to="/intake" testid="nav-new-intake">New Intake</NavItem>
                {user.is_admin && <NavItem to="/admin/leads" testid="nav-admin">Leads</NavItem>}
                {user.is_admin && <NavItem to="/admin/partners" testid="nav-admin-partners">Partners</NavItem>}
                {user.is_admin && <NavItem to="/admin/press" testid="nav-admin-press">Press</NavItem>}
                {user.is_admin && <NavItem to="/admin/reviews" testid="nav-admin-reviews">Reviews</NavItem>}
                {user.is_admin && <NavItem to="/admin/bridge" testid="nav-admin-bridge">Bridge</NavItem>}
                <NavItem to="/account" testid="nav-account">Account</NavItem>
                <button
                  data-testid="nav-logout"
                  onClick={() => { logout(); navigate("/"); }}
                  className="ml-2 font-mono text-xs uppercase tracking-[0.2em] px-3 py-2 border border-ink text-ink hover:bg-ink hover:text-white transition-colors"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <div className="hidden xl:flex items-center">
                  {desktopNav.map((item) =>
                    item.anchor ? (
                      <Link key={item.testid} to={item.to} data-testid={item.testid} className={desktopNavClass}>
                        {item.label}
                      </Link>
                    ) : (
                      <NavLink
                        key={item.testid}
                        to={item.to}
                        data-testid={item.testid}
                        className={({ isActive }) => `${desktopNavClass} ${isActive ? "!border-ink bg-ink text-white" : ""}`}
                      >
                        {item.label}
                      </NavLink>
                    ),
                  )}
                </div>
                <NavItem to="/login" testid="nav-login">Sign in</NavItem>
                <Link
                  to={`/assessment${location.search || ""}`}
                  data-testid="nav-start-assessment"
                  className="ml-2 font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 bg-blueprint text-white border border-blueprint whitespace-nowrap hover:bg-ink hover:border-ink transition-colors"
                >
                  Start Assessment →
                </Link>
              </>
            )}
          </nav>

          {/* MENU TRIGGER: all widths. On desktop it backs up the header
              links (full menu incl. Home); below lg it is the only nav. */}
          <button
            data-testid="mobile-nav-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center justify-center w-11 h-11 border border-ink bg-white text-ink hover:bg-ink hover:text-white transition-colors shrink-0"
          >
            {open ? <X size={20} strokeWidth={1.75} /> : <Menu size={20} strokeWidth={1.75} />}
          </button>
        </div>

        {/* NAV DRAWER (all widths) */}
        {open && (
          <div
            data-testid="mobile-nav-drawer"
            className="absolute top-full left-0 right-0 z-50 bg-white border-b border-ink shadow-[0_8px_0_0_rgba(10,34,64,0.12)] max-h-[calc(100vh-5rem)] overflow-y-auto"
          >
            {user ? (
              <>
                <MobileNavItem to="/dashboard" testid="m-nav-dashboard" onClick={close}>Dashboard</MobileNavItem>
                <MobileNavItem to="/intake" testid="m-nav-new-intake" onClick={close}>New intake</MobileNavItem>
                {user.is_admin && <MobileNavItem to="/admin/leads" testid="m-nav-admin" onClick={close}>Leads</MobileNavItem>}
                {user.is_admin && <MobileNavItem to="/admin/reviews" testid="m-nav-admin-reviews" onClick={close}>Reviews</MobileNavItem>}
                {user.is_admin && <MobileNavItem to="/admin/bridge" testid="m-nav-admin-bridge" onClick={close}>Bridge</MobileNavItem>}
                <MobileNavItem to="/account" testid="m-nav-account" onClick={close}>Account</MobileNavItem>
                <button
                  data-testid="m-nav-logout"
                  onClick={() => { close(); logout(); navigate("/"); }}
                  className="block w-full text-left px-4 py-4 font-mono text-sm uppercase tracking-[0.2em] text-ink hover:bg-paper border-b border-rule"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                {PUBLIC_MOBILE_NAV.map((item) =>
                  item.anchor ? (
                    <MobileAnchorItem key={item.testid} to={item.to} testid={item.testid} onClick={close}>
                      {item.label}
                    </MobileAnchorItem>
                  ) : (
                    <MobileNavItem key={item.testid} to={item.to} testid={item.testid} onClick={close} end={item.exact}>
                      {item.label}
                    </MobileNavItem>
                  ),
                )}
                <MobileNavItem to="/login" testid="m-nav-login" onClick={close}>Sign in</MobileNavItem>
                <Link
                  to={`/assessment${location.search || ""}`}
                  data-testid="m-nav-start-assessment"
                  onClick={close}
                  className="block w-full px-4 py-5 font-mono text-sm uppercase tracking-[0.2em] bg-blueprint text-white text-center hover:bg-ink transition-colors"
                >
                  Start Assessment →
                </Link>
              </>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Sitewide slim sticky header (slides in on scroll) + back-to-top.
          Both hide themselves at the top of the page; the sticky header
          also hides on signed-in app/admin routes. */}
      {!surveyOnly && <StickyMiniNav />}
      <BackToTop />

      {!surveyOnly && (
      <footer className="border-t border-ink mt-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 font-mono text-[13px]">
          <div>
            <img
              src="/brand/initials-navy.png"
              alt="W3RC monogram"
              data-testid="footer-logo"
              className="h-8 w-auto mb-3"
            />
            <div className="font-serif text-xl tracking-tighter">Web3 Ready CLARITY</div>
            <div className="text-ink/75 mt-2">Digital assets risk, made clear.</div>
          </div>
          <div className="text-ink/75">
            <div className="uppercase tracking-[0.2em] text-[11px] mb-2">Resources</div>
            <Link to="/assessment" data-testid="footer-link-assessment" className="block hover:text-ink">CLARITY Assessment</Link>
            <Link to="/webinar" data-testid="footer-link-webinar" className="block hover:text-ink mt-1">The CLARITY Briefing (Free)</Link>
            <Link to="/checklist" data-testid="footer-link-checklist" className="block hover:text-ink mt-1">Clarity Issue-Mapping Starter Kit</Link>
            <Link to="/pricing" data-testid="footer-link-pricing" className="block hover:text-ink mt-1">Pricing</Link>
            <Link to="/partners" data-testid="footer-link-partners" className="block hover:text-ink mt-1">Partnerships</Link>
            <Link to="/faq" data-testid="footer-link-faq" className="block hover:text-ink mt-1">FAQ</Link>
            <Link to="/faq#privacy-and-data-use" data-testid="footer-link-privacy" className="block hover:text-ink mt-1">Privacy &amp; Data Use</Link>
            <Link to="/contact" data-testid="footer-link-contact" className="block hover:text-ink mt-1">Contact &amp; Enterprise</Link>
            <Link to="/login" data-testid="footer-link-signin" className="block hover:text-ink mt-1">Sign In</Link>
          </div>
          <div className="text-ink/75">
            <div className="uppercase tracking-[0.2em] text-[11px] mb-2">The Deliverable</div>
            Your scenario mapped across the relevant legal and regulatory domains, organized by priority. A structured issue map that prepares you for the conversation with outside counsel.
          </div>
          <div className="text-ink/75">
            <div className="uppercase tracking-[0.2em] text-[11px] mb-2">Built For</div>
            {location.pathname === "/mugenvip"
              ? "Founders, foundations, DAOs, and operating teams approaching or navigating a token launch, alongside the counsel and compliance leaders who advise them."
              : location.pathname === "/emtechvip"
              ? "Anyone accountable for digital asset risk: counsel, compliance and risk leaders, and executives at VASPs, banks, fintechs, funds, and regulatory sandbox participants, plus the regulators and policymakers who shape the rules."
              : "Anyone accountable for digital asset risk: counsel, compliance and risk leaders, and executives at VASPs, banks, fintechs, and funds, plus the regulators and policymakers who shape the rules."}
          </div>
        </div>
        <DisclaimerBar />
      </footer>
      )}
    </div>
  );
}
